// Says what to expect from an import before the provider's picker opens: where the files go, that
// they are copies, the limits the API holds an import to, that it carries on without the tab, and
// what each provider brings along of its own. It shows before every import.
//
// Continue is the click the picker's popup rides on, so the drive opens the picker straight from
// it, with nothing in between for the browser to lose the click to.
import PropTypes from 'prop-types'

import Dialog from '@mui/material/Dialog'
import ImportIcon from '@mui/icons-material/AddToDriveOutlined'

import {BaseButton} from '../shared/blocks/Buttons'
import {Text} from '../shared/blocks/Texts'

import './importDialog.scss'

const SHARED_POINTS = [
  'Choose files rather than folders. You can bring in up to 5 GB at a time, as long as your storage has room for it.',
  'Copying carries on if you close this tab. Follow it in the upload tray, and see a summary once it ends.',
  'Each file is imported once. Files you’ve imported before are left out.',
  'A file whose name is already taken here gets a number, like “photo (1).jpg”.'
]

const PROVIDERS = {
  google: {
    name: 'Google Drive',
    points: [
      'Docs, Sheets and Slides arrive as Word, Excel and PowerPoint files, up to 10 MB each. Forms and other Google files can’t be imported.',
      'Google Drive opens in a pop-up. If it doesn’t load, allow pop-ups and third-party cookies for this site.'
    ]
  },
  onedrive: {
    name: 'OneDrive',
    points: [
      'Personal Microsoft accounts only. Work and school accounts aren’t supported yet.',
      'OneDrive opens in a pop-up. The first time, you’ll sign in, then choose your files with one more click.'
    ]
  }
}

const ImportStartDialog = ({provider, destination, onClose, onContinue}) => {
  const {name, points} = PROVIDERS[provider]

  return (
    <Dialog open onClose={onClose} aria-labelledby='import-start-title' slotProps={{paper: {className: 'import-dialog'}}}>
      <div className='dialog-head'>
        <ImportIcon />
        <h2 className='dialog-title' id='import-start-title'>Import from {name}</h2>
      </div>
      <Text className='dialog-body'>
        The files you choose are copied into “{destination}”. The originals stay in {name}.
      </Text>

      <ul className='import-points'>
        {[...SHARED_POINTS, ...points].map(point => <li key={point}>{point}</li>)}
      </ul>

      <div className='dialog-actions'>
        <BaseButton className='dialog-cancel' variant='text' onClick={onClose}>Cancel</BaseButton>
        <BaseButton className='dialog-confirm' variant='text' onClick={onContinue}>Continue to {name}</BaseButton>
      </div>
    </Dialog>
  )
}

ImportStartDialog.propTypes = {
  provider: PropTypes.oneOf(Object.keys(PROVIDERS)).isRequired,
  // The folder the files will land in, by name.
  destination: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired
}

export default ImportStartDialog
